import PersonIcon from "@mui/icons-material/Person";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import type { Nurse } from "../../../utils/models";
import { getShortRole } from "../../../utils/utilFunctions";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";

type Props = {
	/** Everyone matching Role AND Employment type — NOT the eligible subset, so an already-onboarded person stays addable. */
	pool: Nurse[];
	selectedNurseIds: ReadonlySet<number>;
	/** How many will actually be sent to — the same number the Send button shows. */
	selectedCount: number;
	onSelectionChange: (nurseIds: number[]) => void;
	/** How many the filters alone would have chosen — the baseline the "by hand" hint measures against. */
	eligibleCount: number;
	wasInvitedToday: (nurseId: number) => boolean;
};

/** "Will be sent to N", the by-hand hint, and the searchable staff picker. */
export const ResendWelcomeRecipients: FC<Props> = ({ pool, selectedNurseIds, selectedCount, onSelectionChange, eligibleCount, wasInvitedToday }) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();

	const hint =
		selectedCount === eligibleCount
			? t("team.resend_welcome.recipients.matches_filters")
			: selectedCount > eligibleCount
				? t("team.resend_welcome.recipients.added_by_hand", { n: selectedCount - eligibleCount })
				: t("team.resend_welcome.recipients.removed_by_hand", { n: eligibleCount - selectedCount });

	const items = useMemo<DropdownItem<number>[]>(
		() =>
			pool.map((nurse) => ({
				value: nurse.id,
				id: nurse.id,
				searchText: formatPersonName(nurse),
				label: () => (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
						<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{formatPersonName(nurse)}
						</Box>
						<Box component="span" sx={{ fontSize: FONT_SIZE.LABEL_SM, color: roleColor(nurse.role).ink, flexShrink: 0 }}>
							{getShortRole(nurse.role)}
						</Box>
					</Box>
				),
				endAdornment: wasInvitedToday(nurse.id) ? (
					<MetaChip label={t("team.resend_welcome.picker.invited_today")} tone="primary" size="small" dataTestId={`resend-welcome-invited-today-${nurse.id}`} />
				) : undefined,
			})),
		[pool, wasInvitedToday, t, formatPersonName, roleColor],
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.875, fontSize: "14px", fontWeight: 600, color: "primary.dark" }}>
				{t("team.resend_welcome.recipients.will_be_sent_to")}
				<PersonIcon sx={{ fontSize: 16 }} />
				<Box component="span" data-testid="resend-welcome-recipient-count" sx={{ fontVariantNumeric: "tabular-nums" }}>
					{selectedCount}
				</Box>
			</Box>
			<Box component="span" data-testid="resend-welcome-recipient-hint" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
				{hint}
			</Box>
			<SystemDropdown
				data-testid="resend-welcome-staff-picker"
				items={items}
				multiple
				selectedItems={Array.from(selectedNurseIds)}
				onChangeMultiple={onSelectionChange}
				label={t("team.resend_welcome.picker.trigger")}
				trigger="button"
				// The trigger names the action, not the selection — the count already sits to its left.
				renderValue={() => null}
				search={{ placeholder: t("team.resend_welcome.picker.search_placeholder") }}
				// Off here: everyone matching the filters starts CHECKED, so searching is how you find one
				// person to REMOVE — and auto-checking the sole match silently undoes that click.
				autoSelectSingleMatch={false}
				// `allOption=""` suppresses "Select all": the filters above are how you select everyone, and a
				// select-all here would silently defeat the login-status narrowing the dialog's subtitle claims.
				allOption=""
				// The one thing the house footer ("N selected") cannot say: how much of the pool is on screen.
				panelNote={({ shown, total, query }) =>
					query ? t("team.resend_welcome.picker.shown_count", { shown, total }) : t("team.resend_welcome.picker.match_count", { n: total })
				}
				menuWidth={336}
				// The count already sits to the left of this control, so the trigger's own badge would say it twice.
				hideTriggerCount
				selectSx={{ marginInlineStart: "auto", width: 236, height: 40, justifyContent: "space-between", fontSize: "14px" }}
			/>
		</Box>
	);
};
